// Experience, Featured, Campaigns, InstagramFeed (real post UI), About, Contact.

import { useState } from 'react';
import { useContent, useLang, TONE_BG, UI } from './content.jsx';
import { Reveal, PulseDot, Placeholder, CoffeeArt } from './primitives.jsx';
import { RevealText, ParallaxCard, Counter } from './animations.jsx';

export function Experience() {
  const c = useContent();
  const { t } = useLang();
  return (
    <section className="relative py-28 md:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-6">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-tan mb-5">— The Glamm Experience</div>
            <h2 className="font-display text-[34px] md:text-[64px] leading-[1.02] tracking-tight text-espresso">
              {t({ tr: 'Sadece bir ', en: 'Not just ' })}
              <em className="italic text-cocoa gradient-sheen">{t({ tr: 'kahve', en: 'coffee' })}</em>
              {t({ tr: ' değil,', en: ',' })}<br/>
              {t({ tr: 'küçük bir ', en: 'a little ' })}
              <em className="italic text-rose">{t({ tr: 'ritüel', en: 'ritual' })}</em>.
            </h2>
          </Reveal>
          <Reveal className="md:col-span-5 md:col-start-8" delay={2}>
            <p className="text-[15.5px] leading-relaxed text-espresso/75 max-w-[460px]">
              {t({ tr: 'Glamm, günün belki de en hızlı geçen anını yavaşlatmak için var. Bardak, tabak, ışık, koku — hepsi bir arada çalışır.',
                   en: 'Glamm exists to slow down the fastest moment of your day. Cup, plate, light, aroma — they all work together.' })}
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-4 gap-5 mt-16">
          {c.experience.map((it, i) => (
            <Reveal key={i} delay={(i % 4) + 1}>
              <div className="group relative rounded-3xl bg-white/60 border border-espresso/10 p-6 h-full hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(74,46,30,0.14)] transition-all duration-500 overflow-hidden">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{ background: 'radial-gradient(circle at 20% 20%, rgba(245,220,212,0.4) 0%, transparent 60%)' }}/>
                <div className="relative">
                  <div className="font-mono text-[11px] tracking-[0.2em] text-tan">{it.k}</div>
                  <div className="font-display text-[22px] text-espresso mt-6">{t(it.t)}</div>
                  <p className="text-[13.5px] text-espresso/70 mt-2 leading-relaxed">{t(it.d)}</p>
                  <div className="absolute right-0 top-0 h-7 w-7 rounded-full border border-espresso/20 grid place-items-center text-[11px] text-espresso/60 opacity-0 group-hover:opacity-100 transition">→</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Featured() {
  const c = useContent();
  const { t } = useLang();
  return (
    <section className="relative py-20 md:py-28 bg-latte/40">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <Reveal>
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-tan mb-4">
              {t({ tr: '— Öne Çıkanlar', en: '— Featured' })}
            </div>
            <h2 className="font-display text-[30px] md:text-[54px] leading-tight text-espresso">
              <RevealText>{t({ tr: 'Bu hafta masada.', en: 'On the table this week.' })}</RevealText>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <a href="#menu" className="inline-flex items-center gap-2 text-[13px] text-espresso/80 hover:text-espresso">
              {t({ tr: 'Tüm menü', en: 'Full menu' })} <span>→</span>
            </a>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {c.featured.map((it, i) => {
            const menuItem = it.menuId != null ? c.menu.items.find(m => m.id === it.menuId) : null;
            const resolved = {
              name:  (it.name?.tr || it.name?.en) ? it.name : (menuItem?.name || it.name),
              image: it.image || menuItem?.image || '',
              tone:  it.tone || menuItem?.tone || 'cream',
              label: (it.label?.tr || it.label?.en)
                ? it.label
                : (menuItem?.tag ? { tr: menuItem.tag, en: menuItem.tag } : it.label),
            };
            return (
              <Reveal key={i} delay={(i % 4) + 1}>
                <FeaturedCard item={resolved} t={t}/>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ item, t }) {
  const name = t(item.name);
  const label = t(item.label);
  return (
    <ParallaxCard intensity={8} className="group relative">
      <div className="relative rounded-3xl overflow-hidden aspect-[3/4] shadow-[0_14px_40px_rgba(74,46,30,0.08)] group-hover:shadow-[0_30px_70px_rgba(74,46,30,0.22)] transition-shadow duration-700"
        style={{ background: TONE_BG[item.tone] }}>
        {item.image ? (
          <img src={item.image} alt={name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.08]"/>
        ) : (
          <Placeholder tone={item.tone} label={label} ratio="3/4" rounded="rounded-none" className="absolute inset-0"/>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent"/>
        <div className="absolute bottom-0 inset-x-0 p-4 text-white"
          style={{ transform: 'translate3d(calc(var(--px, 0) * -0.4), calc(var(--py, 0) * -0.4), 30px)' }}>
          <div className="font-display text-[18px] md:text-[20px] leading-none drop-shadow">{name}</div>
          <div className="mt-1 font-mono text-[9px] tracking-[0.2em] opacity-80 flex items-center gap-1">
            <span>VIEW</span>
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">→</span>
          </div>
        </div>
        <div className="absolute inset-0 rounded-3xl ring-0 group-hover:ring-1 ring-white/60 transition pointer-events-none"/>
      </div>
    </ParallaxCard>
  );
}

export function Campaigns() {
  const c = useContent();
  const { t } = useLang();
  const h = c.campaigns.hero;
  const sides = c.campaigns.side;
  return (
    <section className="relative py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 grid md:grid-cols-12 gap-6">
        <Reveal className="md:col-span-7">
          <div className="relative rounded-[32px] overflow-hidden min-h-[320px] md:min-h-[360px] p-6 md:p-12 flex flex-col justify-between group"
            style={{ background: 'linear-gradient(135deg, #4a2e1e 0%, #8b5a3c 100%)' }}>
            <div className="absolute -right-20 -bottom-20 w-[400px] h-[400px] rounded-full transition-transform duration-700 group-hover:scale-110"
              style={{ background: 'radial-gradient(circle, rgba(245,220,212,0.45) 0%, transparent 70%)' }}/>
            <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.2) 0%, transparent 40%)',
            }}/>
            <div className="relative">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-tan">— {t(h.label)}</div>
              <div className="font-display text-[30px] md:text-[54px] leading-[1.02] md:leading-[1.0] text-cream mt-4 whitespace-pre-line">
                <RevealText>{t(h.title)}</RevealText>
              </div>
              <p className="text-[14px] text-cream/75 mt-4 max-w-[440px]">{t(h.body)}</p>
            </div>
            <div className="relative flex flex-wrap items-center gap-3 mt-8">
              {h.cta1 && (
                <CampaignButton href={h.cta1Link} primary>{t(h.cta1)}</CampaignButton>
              )}
              {h.cta2 && (
                <CampaignButton href={h.cta2Link}>{t(h.cta2)}</CampaignButton>
              )}
            </div>
          </div>
        </Reveal>
        <div className="md:col-span-5 grid gap-6">
          {sides.map((s, i) => {
            const inner = (
              <div className="warm-card rounded-[28px] p-7 min-h-[170px] relative overflow-hidden group hover:-translate-y-1 transition-transform duration-500 h-full"
                style={{ background: TONE_BG[s.tone] || TONE_BG.cream }}>
                <div className="font-mono text-[10.5px] tracking-[0.25em] uppercase text-espresso/70">{t(s.label)}</div>
                <div className="font-display text-[26px] text-espresso mt-3 leading-tight whitespace-pre-line">{t(s.title)}</div>
                <div className="text-[12.5px] text-espresso/65 mt-2">{t(s.note)}</div>
                <div className="absolute right-5 bottom-5 h-12 w-12 rounded-full grid place-items-center bg-espresso text-cream text-sm group-hover:translate-x-1 transition">→</div>
              </div>
            );
            return (
              <Reveal key={i} delay={i + 1}>
                {s.link ? (
                  <a href={s.link} {...(isExternal(s.link) ? { target: '_blank', rel: 'noreferrer' } : {})} className="block">
                    {inner}
                  </a>
                ) : inner}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function isExternal(href) {
  return /^https?:\/\//i.test(href || '');
}

function CampaignButton({ href, primary, children }) {
  const cls = primary
    ? 'rounded-full bg-cream text-espresso px-5 py-3 text-[13px] hover:bg-blush transition'
    : 'rounded-full border border-cream/30 text-cream px-5 py-3 text-[13px] hover:bg-white/10 transition';
  if (!href) return <button className={cls}>{children}</button>;
  return (
    <a href={href} {...(isExternal(href) ? { target: '_blank', rel: 'noreferrer' } : {})} className={cls}>
      {children}
    </a>
  );
}

/* ========= Instagram ========= */

export function InstagramFeed() {
  const c = useContent();
  const { t } = useLang();
  const ig = c.instagram;
  const stories = c.stories;
  const [open, setOpen] = useState(null);

  return (
    <section className="relative py-20 md:py-28 bg-cream overflow-hidden">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <Reveal>
            <div className="flex items-center gap-3 font-mono text-[11px] tracking-[0.25em] uppercase text-tan mb-4">
              <PulseDot color="#e9b7a6"/> {ig.handle} · {t(UI.ig.live)}
            </div>
            <h2 className="font-display text-[30px] md:text-[54px] leading-tight text-espresso">
              {t({ tr: 'Sayfayı ', en: 'Scroll the ' })}
              <em className="italic text-cocoa">{t(UI.ig.emTitle)}</em>
              {t({ tr: ', tadı gözünle tat.', en: ', taste with your eyes.' })}
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <div className="flex items-center gap-3">
              {ig.followers && (
                <div className="text-right hidden md:block">
                  <div className="font-display text-[20px] text-espresso"><Counter value={ig.followers}/></div>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-tan">{t(UI.ig.followers)}</div>
                </div>
              )}
              <a href={ig.url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-espresso text-cream px-5 py-2.5 text-[12.5px] tracking-wide hover:bg-ink transition">
                <InstagramIcon/>
                {t(UI.ig.follow)}
              </a>
            </div>
          </Reveal>
        </div>


        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {ig.posts.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) + 1}>
              <InstagramPost post={p} handle={ig.handle} t={t} onOpen={() => setOpen(p)}/>
            </Reveal>
          ))}
        </div>

        <Reveal delay={2}>
          <div className="mt-10 flex justify-center">
            <a href={ig.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-espresso/20 px-5 py-2.5 text-[13px] text-espresso hover:bg-white/60 transition">
              {t(UI.ig.seeAll)} →
            </a>
          </div>
        </Reveal>
      </div>

      {open && <InstagramModal post={open} handle={ig.handle} bio={ig.bio} t={t} onClose={() => setOpen(null)}/>}
    </section>
  );
}

function InstagramPost({ post, handle, onOpen, t }) {
  const c = useContent();
  const brandLogo = c.brand?.logo || '/assets/glamm.png';
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [doubleTap, setDoubleTap] = useState(false);
  const caption = t(post.caption);
  const tag = post.tag || '';
  const handleName = (handle || '').replace('@', '') || 'glammcoffee';

  const onDouble = () => {
    setLiked(true);
    setDoubleTap(true);
    setTimeout(() => setDoubleTap(false), 700);
  };

  return (
    <div className="rounded-3xl bg-white border border-espresso/10 overflow-hidden shadow-[0_10px_30px_rgba(74,46,30,0.08)] hover:shadow-[0_24px_60px_rgba(74,46,30,0.18)] hover:-translate-y-1 transition-all duration-500">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="relative h-9 w-9 rounded-full p-[2px]"
            style={{ background: 'conic-gradient(from 0deg, #c9a575, #e9b7a6, #8b5a3c, #c9a575)' }}>
            <div className="h-full w-full rounded-full bg-white p-[2px]">
              <img src={brandLogo} className="h-full w-full rounded-full object-cover" alt="Glamm"/>
            </div>
          </div>
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-espresso flex items-center gap-1">
              {handleName}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#3897F0"><path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.26 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.68-.88 3.34-2.19c1.39.45 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.36-6.2 6.77z"/></svg>
            </div>
            {(tag || post.date) && (
              <div className="text-[10.5px] text-espresso/60 font-mono tracking-wide">{tag}{post.date ? ` · ${post.date}` : ''}</div>
            )}
          </div>
        </div>
        {post.postUrl && (
          <a href={post.postUrl} target="_blank" rel="noreferrer" title={t({ tr: 'Instagram\'da aç', en: 'Open on Instagram' })}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-latte/40 transition text-espresso/60">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        )}
      </div>

      <div className="relative aspect-square cursor-pointer overflow-hidden"
        onClick={onOpen} onDoubleClick={onDouble}
        style={{ background: TONE_BG[post.tone] }}>
        {post.image ? (
          <img src={post.image} alt={caption} className="absolute inset-0 w-full h-full object-cover"/>
        ) : (
          <Placeholder tone={post.tone} label={(tag || 'GLAMM').toUpperCase()} ratio="1/1" rounded="rounded-none" className="absolute inset-0"/>
        )}
        {doubleTap && (
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <svg width="120" height="120" viewBox="0 0 24 24" fill="white"
              style={{ filter: 'drop-shadow(0 0 20px rgba(0,0,0,0.4))', animation: 'heartPop 0.7s ease-out forwards' }}>
              <path d="M12 21s-7-4.5-9-9c-1.2-2.7 0-6 3-6 2 0 3 1 3 2 0-1 1-2 3-2 3 0 4.2 3.3 3 6-2 4.5-9 9-9 9z" transform="translate(3, 1)"/>
            </svg>
          </div>
        )}
      </div>

      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setLiked(!liked)}
            className={`h-8 w-8 grid place-items-center transition ${liked ? 'text-rose-500' : 'text-espresso hover:text-rose-400'}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
          <button onClick={onOpen} className="h-8 w-8 grid place-items-center text-espresso hover:text-cocoa">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
          </button>
          {post.postUrl && (
            <a href={post.postUrl} target="_blank" rel="noreferrer" className="h-8 w-8 grid place-items-center text-espresso hover:text-cocoa">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </a>
          )}
        </div>
        <button onClick={() => setSaved(!saved)}
          className={`h-8 w-8 grid place-items-center ${saved ? 'text-espresso' : 'text-espresso hover:text-cocoa'}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
        </button>
      </div>

      <div className="px-4 pb-4 pt-1">
        {post.likes > 0 && (
          <div className="text-[13px] font-semibold text-espresso">
            {(post.likes + (liked ? 1 : 0)).toLocaleString('tr-TR')} {t(UI.ig.likes)}
          </div>
        )}
        {caption && (
          <div className="text-[13px] text-espresso/85 mt-1 leading-relaxed line-clamp-2">
            <span className="font-semibold">{handleName}</span>{' '}
            {caption}
          </div>
        )}
        {post.comments > 0 && (
          <button onClick={onOpen} className="text-[12px] text-espresso/55 mt-1 hover:text-espresso/80">
            {post.comments} {t(UI.ig.viewAllComments)}
          </button>
        )}
      </div>
    </div>
  );
}

function InstagramModal({ post, handle, bio, onClose, t }) {
  const c = useContent();
  const brandLogo = c.brand?.logo || '/assets/glamm.png';
  const handleName = (handle || '').replace('@', '') || 'glammcoffee';
  const caption = t(post.caption);
  return (
    <div className="fixed inset-0 z-[88] grid place-items-center bg-ink/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="relative w-full max-w-4xl bg-white rounded-3xl overflow-hidden shadow-2xl grid md:grid-cols-2"
        style={{ animation: 'menuCardIn .35s cubic-bezier(.2,.7,.2,1)' }}>
        <div className="relative aspect-square md:aspect-auto md:min-h-[540px]"
          style={{ background: TONE_BG[post.tone] }}>
          {post.image ? (
            <img src={post.image} alt={caption} className="absolute inset-0 w-full h-full object-cover"/>
          ) : (
            <Placeholder tone={post.tone} label={(post.tag || 'GLAMM').toUpperCase()} ratio="1/1" rounded="rounded-none" className="absolute inset-0"/>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-espresso/10">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-full p-[2px]" style={{ background: 'conic-gradient(from 0deg, #c9a575, #e9b7a6, #8b5a3c, #c9a575)' }}>
                <div className="h-full w-full rounded-full bg-white p-[2px]">
                  <img src={brandLogo} className="h-full w-full rounded-full object-cover" alt="Glamm"/>
                </div>
              </div>
              <div className="leading-tight">
                <div className="text-[13px] font-semibold text-espresso">{handleName}</div>
                <div className="text-[10.5px] text-espresso/55">{t(bio)}</div>
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-full grid place-items-center hover:bg-latte/40">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            {caption && (
              <div className="flex items-start gap-2.5">
                <div className="h-8 w-8 rounded-full flex-none bg-latte grid place-items-center text-[11px] font-mono text-espresso">G</div>
                <div className="text-[13.5px] text-espresso leading-relaxed">
                  <span className="font-semibold mr-1">{handleName}</span>
                  {caption}
                </div>
              </div>
            )}
            {post.tag && (
              <div className="text-[12px] text-espresso/60">#{String(post.tag).replace(/\s/g, '')}</div>
            )}
            {post.postUrl && (
              <a href={post.postUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-espresso text-cream px-4 py-2 text-[12.5px] hover:bg-ink transition">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
                {t({ tr: 'Instagram\'da aç', en: 'Open on Instagram' })}
              </a>
            )}
          </div>
          <div className="px-5 py-3 border-t border-espresso/10 flex items-center justify-between gap-4">
            {post.likes > 0 && (
              <div>
                <div className="text-[13px] font-semibold text-espresso">{post.likes.toLocaleString('tr-TR')} {t(UI.ig.likes)}</div>
                {post.date && <div className="mt-0.5 text-[10.5px] text-espresso/55 uppercase tracking-wider font-mono">{post.date}</div>}
              </div>
            )}
            <div className="ml-auto flex items-center gap-3 text-espresso/80">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InstagramIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2" y="2" width="20" height="20" rx="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  );
}

/* ========= About + Contact ========= */

export function About() {
  const c = useContent();
  const { t } = useLang();
  const a = c.about;
  return (
    <section id="about" className="relative py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 grid md:grid-cols-12 gap-10 items-center">
        <Reveal className="md:col-span-5">
          <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-tan mb-5">{t(a.kicker)}</div>
          <h2 className="font-display text-[40px] md:text-[60px] leading-[1.02] text-espresso"><RevealText>{t(a.title)}</RevealText></h2>
          <p className="mt-6 text-[15.5px] leading-relaxed text-espresso/75 max-w-[460px]">{t(a.body)}</p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            {a.stats.map(([n, l], i) => (
              <div key={i}>
                <div className="font-display text-[34px] text-espresso leading-none">
                  <Counter value={t(n)} duration={1800}/>
                </div>
                <div className="text-[11px] tracking-wide text-espresso/60 mt-2 uppercase">{t(l)}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="md:col-span-7 grid grid-cols-6 grid-rows-2 gap-4 md:gap-5 h-[520px]">
          <Reveal className="col-span-4 row-span-2"><Placeholder tone="rose"   label="LIFESTYLE · MORNING" ratio="auto" className="h-full hover:scale-[1.02] transition-transform duration-700"/></Reveal>
          <Reveal delay={1} className="col-span-2 row-span-1"><Placeholder tone="latte"  label="BARISTA POUR" ratio="auto" className="h-full hover:scale-[1.02] transition-transform duration-700"/></Reveal>
          <Reveal delay={2} className="col-span-2 row-span-1"><Placeholder tone="butter" label="OVEN FRESH"    ratio="auto" className="h-full hover:scale-[1.02] transition-transform duration-700"/></Reveal>
        </div>
      </div>
    </section>
  );
}

export function Contact() {
  const c = useContent();
  const { t } = useLang();
  const ct = c.contact;
  const b = c.brand;
  return (
    <section id="contact" className="relative py-24 md:py-32 bg-latte/40">
      <div className="mx-auto max-w-7xl px-6 grid md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-5">
          <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-tan mb-5">{t(ct.kicker)}</div>
          <h2 className="font-display text-[40px] md:text-[56px] leading-[1.02] text-espresso">{t(ct.title)}</h2>
          <p className="mt-5 text-[15px] leading-relaxed text-espresso/75 max-w-[420px]">{t(ct.body)}</p>
          <div className="mt-8 space-y-3 text-[14px] text-espresso/80">
            <div className="flex items-center gap-3"><span className="font-mono text-[10.5px] tracking-widest text-tan w-14">MAIL</span> {b.email}</div>
            <div className="flex items-center gap-3"><span className="font-mono text-[10.5px] tracking-widest text-tan w-14">IG</span> {b.instagram}</div>
            <div className="flex items-center gap-3"><span className="font-mono text-[10.5px] tracking-widest text-tan w-14">TEL</span> {b.phone}</div>
          </div>
        </Reveal>
        <Reveal delay={2} className="md:col-span-7">
          <form className="rounded-3xl bg-white/70 border border-espresso/10 p-7 md:p-9 grid gap-5"
            onSubmit={e => { e.preventDefault(); alert(t(UI.contact.thanks)); }}>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={t(UI.contact.name)} placeholder={t(UI.contact.namePh)}/>
              <Field label={t(UI.contact.email)} placeholder={t(UI.contact.emailPh)}/>
            </div>
            <Field label={t(UI.contact.subject)} placeholder={t(UI.contact.subjectPh)}/>
            <div>
              <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-tan mb-2">{t(UI.contact.message)}</div>
              <textarea rows="4" placeholder={t(UI.contact.messagePh)}
                className="w-full bg-transparent border-b border-espresso/15 pb-2 text-[14px] outline-none focus:border-espresso/40 resize-none"/>
            </div>
            <div className="flex items-center justify-between pt-2">
              <div className="text-[11px] text-espresso/55">{t(UI.contact.kvkk)}</div>
              <button type="submit" className="rounded-full bg-espresso text-cream px-6 py-3 text-[13px] hover:bg-ink transition">{t(UI.contact.submit)} →</button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, placeholder }) {
  return (
    <div>
      <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-tan mb-2">{label}</div>
      <input placeholder={placeholder} className="w-full bg-transparent border-b border-espresso/15 pb-2 text-[14px] outline-none focus:border-espresso/40"/>
    </div>
  );
}
